import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import { Button } from "./components/ui/button";
import { motion } from "framer-motion";
import AffiliatesPage from "./components/AffiliatesPage";
import PromptGeneratorPage from "./components/PromptGeneratorPage";
import FoldersPage from "./components/FoldersPage";
import PromptTemplatesPage from "./components/PromptTemplatesPage";
import BulkActionsPage from "./components/BulkActionsPage";
import UIPage from "./components/UIPage";
import UIExtensionPage from "./components/UIExtensionPage";

const API_URL = 'https://usz4akc9rf.execute-api.us-east-1.amazonaws.com/Prod';

// Sticky Header Component
const StickyHeader = ({ onButtonClick }) => (
  <motion.div 
    initial={{ y: -100 }}
    animate={{ y: 0 }}
    transition={{ duration: 0.5 }}
    className="hidden md:block fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-gray-200 z-50 shadow-sm"
  >
    <div className="max-w-6xl mx-auto px-4 py-3 flex justify-between items-center">
      <div className="flex items-center gap-3">
        <img src="/icon.png" alt="ChatGPT Power-Up" className="w-8 h-8" />
        <span className="font-semibold text-gray-900">ChatGPT Power-Up</span>
      </div>
      <a
        href="https://chromewebstore.google.com/detail/chatgpt-power-up/ooleaojggfoigcdkodigbcjnabidihgi"
        onClick={() => onButtonClick && onButtonClick('chrome_sticky')}
        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
      >
        <img src="/chrome.png" alt="Chrome" className="w-5 h-5" />
        Install Now - Free
      </a>
    </div>
  </motion.div>
);

const DownloadButtons = ({ delay = 0.6, onButtonClick }) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay }}
    className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-3xl mx-auto"
  >

    {/* Chrome */}
    <a
      href="https://chromewebstore.google.com/detail/chatgpt-power-up/ooleaojggfoigcdkodigbcjnabidihgi"
      className="group no-underline"
      onClick={e => {
        if (onButtonClick) onButtonClick('chrome');
      }}
    >
      <div className="flex flex-col items-center p-6 rounded-xl hover:bg-white hover:shadow-xl transition-all duration-300 h-full">
        <img src="/chrome.png" alt="Chrome Web Store" className="w-16 h-16 mb-4 group-hover:scale-110 transition-transform duration-300" />
        <Button className="bg-blue-600 hover:bg-blue-700 text-white shadow-md hover:shadow-lg transition-all duration-300 w-full mb-3">
          Download for free
        </Button>
        <span className="text-gray-600 font-medium">Chrome Web Store</span>
      </div>
    </a>

    {/* Firefox */}
    <div className="group">
      <div className="flex flex-col items-center p-6 rounded-xl hover:bg-white hover:shadow-xl transition-all duration-300 h-full">
        <img src="/firefox.png" alt="Firefox Add-ons" className="w-16 h-16 mb-4 group-hover:scale-110 transition-transform duration-300" />
        <Button
          className="bg-orange-600 hover:bg-orange-700 text-white shadow-md hover:shadow-lg transition-all duration-300 w-full mb-3"
          onClick={() => onButtonClick && onButtonClick('firefox')}
        >
          Coming Soon
        </Button>
        <span className="text-gray-600 font-medium">Firefox Add-ons</span>
      </div>
    </div>

    {/* Edge */}
    <div className="group">
      <div className="flex flex-col items-center p-6 rounded-xl hover:bg-white hover:shadow-xl transition-all duration-300 h-full">
        <img src="/edge.png" alt="Edge Add-ons" className="w-16 h-16 mb-4 group-hover:scale-110 transition-transform duration-300" />
        <Button
          className="bg-teal-600 hover:bg-teal-700 text-white shadow-md hover:shadow-lg transition-all duration-300 w-full mb-3"
          onClick={() => onButtonClick && onButtonClick('edge')}
        >
          Coming Soon
        </Button>
        <span className="text-gray-600 font-medium">Edge Add-ons</span>
      </div>
    </div>
  </motion.div>
);

// FAQ Component
const FAQ = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay: 1.0 }}
    className="mt-24 max-w-3xl mx-auto"
  >
    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h2>
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-xl shadow-md">
        <h3 className="font-semibold text-gray-900 mb-2">Is ChatGPT Power-Up free?</h3>
        <p className="text-gray-600">Yes – the core features are completely free to use. You can download it at no cost from the Chrome Web Store.</p>
      </div>
      <div className="bg-white p-6 rounded-xl shadow-md">
        <h3 className="font-semibold text-gray-900 mb-2">Does it store or see my chats?</h3>
        <p className="text-gray-600">No. Your data stays local and private. The extension only enhances the ChatGPT interface without accessing your conversation content.</p>
      </div>
      <div className="bg-white p-6 rounded-xl shadow-md">
        <h3 className="font-semibold text-gray-900 mb-2">How do I install it?</h3>
        <p className="text-gray-600">Simply click "Add to Chrome" and it will install directly from the Chrome Web Store. After installation, refresh ChatGPT to see the new features.</p>
      </div>
      <div className="bg-white p-6 rounded-xl shadow-md">
        <h3 className="font-semibold text-gray-900 mb-2">Will it work with my existing ChatGPT account?</h3>
        <p className="text-gray-600">Absolutely! It works with any ChatGPT account and integrates seamlessly with the native UI without any setup required.</p>
      </div>
    </div>
  </motion.div>
);

function Landing() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [source, setSource] = useState('without_query_param');

  useEffect(() => {
    // Update document title and meta description for SEO
    document.title = "ChatGPT Power-Up: Organize Chats with Folders & Prompt Templates";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'ChatGPT Power-Up is a Chrome extension that supercharges ChatGPT with folders, prompt templates, bulk actions, and more. Organize chats, save time, and boost your productivity inside ChatGPT\'s UI.');
    }

    // Get the source from URL query parameters
    const urlParams = new URLSearchParams(window.location.search);
    const src = urlParams.get('source') || 'without_query_param';
    setSource(src);
    
    // Get referrer information
    const referrer = document.referrer || 'direct';
    
    // Call the tracking endpoint with source and referrer combined
    fetch(`${API_URL}/track?source=${encodeURIComponent(src + ', referrer: ' + referrer)}`)
      .catch(error => console.error('Error tracking source:', error));
  }, []);

  // Handler for button clicks
  const handleButtonClick = (browser) => {
    fetch(`${API_URL}/track?button=${encodeURIComponent(source + '_' + browser)}`)
      .catch(error => console.error('Error tracking button click:', error));
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 px-4 py-12">
      {/* Sticky Header */}
      <StickyHeader onButtonClick={handleButtonClick} />

      {/* Navigation Header */}
      <div className="absolute top-0 right-0 md:top-6 md:right-6 z-40">
        <Link to={`/affiliate${source !== 'without_query_param' ? `?source=${encodeURIComponent(source)}` : ''}`}>
          <Button 
            variant="outline" 
            className="text-xs px-2 py-1 font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 md:text-base md:px-4 md:py-2"
          >
            Become an Affiliate
          </Button>
        </Link>
      </div>

      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-3xl w-full text-center mt-16"
      >
        {/* Extension Icon and Name */}
        <div className="mb-16">
          <motion.div 
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="inline-block"
          >
            <img src="/icon.png" alt="ChatGPT Power-Up Icon" className="w-28 h-28 mb-4" />
          </motion.div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">10x your productivity on ChatGPT</h1>
        </div>

        {/* Tagline */}
        <motion.p 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto mb-16 leading-relaxed"
        >
          Make ChatGPT faster, easier, and way more useful by adding folders, reusable prompt snippets, bulk chat actions, and more — directly inside the native UI.
        </motion.p>

        {/* Features Navigation */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mb-16"
        >
          <div className="flex flex-wrap justify-center gap-4 max-w-6xl mx-auto">
            <Link to="/chatgpt-folders" className="group">
              <div className="bg-white p-4 rounded-lg shadow-md hover:shadow-lg transition-all text-center w-32 h-32">
                <div className="text-2xl mb-2">📁</div>
                <h3 className="font-semibold text-gray-900 text-xs group-hover:text-blue-600 transition-colors">Folders</h3>
                <p className="text-xs text-gray-600">Organize chats</p>
              </div>
            </Link>
            <Link to="/chatgpt-prompt-generator" className="group">
              <div className="bg-white p-4 rounded-lg shadow-md hover:shadow-lg transition-all text-center w-32 h-32">
                <div className="text-2xl mb-2">⚡</div>
                <h3 className="font-semibold text-gray-900 text-xs group-hover:text-blue-600 transition-colors">Prompt Snippets</h3>
                <p className="text-xs text-gray-600">Save & reuse prompts</p>
              </div>
            </Link>
            <Link to="/chatgpt-prompt-templates" className="group">
              <div className="bg-white p-4 rounded-lg shadow-md hover:shadow-lg transition-all text-center w-32 h-32">
                <div className="text-2xl mb-2">📝</div>
                <h3 className="font-semibold text-gray-900 text-xs group-hover:text-blue-600 transition-colors">Prompt Templates</h3>
                <p className="text-xs text-gray-600">Save your own templates</p>
              </div>
            </Link>
            <Link to="/chatgpt-bulk-actions" className="group">
              <div className="bg-white p-4 rounded-lg shadow-md hover:shadow-lg transition-all text-center w-32 h-32">
                <div className="text-2xl mb-2">🗑️</div>
                <h3 className="font-semibold text-gray-900 text-xs group-hover:text-blue-600 transition-colors">Bulk Actions</h3>
                <p className="text-xs text-gray-600">Delete multiple chats</p>
              </div>
            </Link>
            <Link to="/chatgpt-ui" className="group">
              <div className="bg-white p-4 rounded-lg shadow-md hover:shadow-lg transition-all text-center w-32 h-32">
                <div className="text-2xl mb-2">🎨</div>
                <h3 className="font-semibold text-gray-900 text-xs group-hover:text-blue-600 transition-colors">UI Enhancement</h3>
                <p className="text-xs text-gray-600">Better interface</p>
              </div>
            </Link>
          </div>
        </motion.div>

        {/* Browser Extension Buttons */}
        <DownloadButtons onButtonClick={handleButtonClick} />

        <div className="w-full max-w-2xl mx-auto mt-24 mb-24">
          <div className="h-px bg-gray-200"></div>
        </div>

        {/* Feature Showcase - Favorite Instructions */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-24 max-w-3xl mx-auto"
        >
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">Save and Reuse Your Favorite Instructions</h2>
          <div className="rounded-xl overflow-hidden shadow-2xl">
            <img 
              src="/favorite-instructions.gif" 
              alt="ChatGPT Power-Up Favorite Instructions Feature" 
              className="w-full h-auto"
            />
          </div>
        </motion.div>

        <div className="w-full max-w-2xl mx-auto mt-24 mb-24">
          <div className="h-px bg-gray-200"></div>
        </div>

        {/* Feature Screenshots Row */}
        <div className="flex flex-col md:flex-row items-center justify-center gap-12 mt-24">
          {/* Folders Feature Screenshot */}
          <div className="flex flex-col items-center max-w-[15rem] flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">Keep organized with folders integrated in the native ChatGPT UI</h3>
            <img 
              src="/folders.png" 
              alt="Folders feature screenshot" 
              className="rounded-lg shadow-lg w-full"
            />
          </div>
          {/* Vertical Separator */}
          <div className="hidden md:flex h-48 w-px bg-gray-200 mx-6"></div>
          {/* Bulk Actions Feature Screenshot */}
          <div className="flex flex-col items-center max-w-[15rem] flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">Perform bulk actions on your chats for ultimate productivity</h3>
            <img 
              src="/bulk-actions.png" 
              alt="Bulk actions feature screenshot" 
              className="rounded-lg shadow-lg w-full"
            />
          </div>
        </div>
        {/* And more! */}
        <div className="flex justify-center mt-10">
          <span className="text-2xl font-bold">...and more!</span>
        </div>
        <div className="w-full max-w-2xl mx-auto mt-24 mb-24">
          <div className="h-px bg-gray-200"></div>
        </div>

        {/* Social Proof Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.9 }}
          className="mt-24 max-w-4xl mx-auto"
        >
          {/* Company Logos */}
          <div className="mb-12">
            <p className="text-gray-600 text-center mb-6">Used by professionals from leading companies</p>
            <div className="flex flex-wrap justify-center items-center gap-8 relative">
              <img src="/amazon-logo.png" alt="Amazon" className="h-8 opacity-70 hover:opacity-100 transition-opacity" />
              <img src="/msft-logo.png" alt="Microsoft" className="h-8 opacity-70 hover:opacity-100 transition-opacity" />
              <img src="/etoro-logo.png" alt="eToro" className="h-8 opacity-70 hover:opacity-100 transition-opacity" />
              <img src="/check-point-logo.png" alt="Check Point" className="h-8 opacity-70 hover:opacity-100 transition-opacity" />
              <img src="/lightricks-logo.png" alt="Lightricks" className="h-8 opacity-70 hover:opacity-100 transition-opacity" />
              <div className="flex items-center gap-2 text-gray-400">
                <span className="text-sm">and many more</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>

          {/* Chrome Store Rating */}
          <div className="flex justify-center items-center gap-3 mb-12">
            <div className="text-yellow-400 flex">
              {[...Array(5)].map((_, i) => (
                <svg key={i} className="w-6 h-6 fill-current" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
            </div>
            <span className="text-xl font-semibold text-gray-700">5.0 on Chrome Web Store</span>
          </div>

          {/* Reddit Testimonials Carousel */}
          <div className="relative max-w-3xl mx-auto mb-12">
            <motion.div 
              className="overflow-hidden"
              initial={false}
            >
              <motion.div 
                className="flex"
                animate={{ x: `${-currentSlide * 100}%` }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
              >
                {/* Testimonial 1 */}
                <div className="w-full flex-shrink-0 px-4">
                  <div className="bg-white p-6 rounded-xl shadow-md">
                    <p className="text-gray-700 italic">"Exactly what ChatGPT was missing. The folder organization and prompt snippets have completely transformed my workflow."</p>
                    <div className="flex items-center gap-2 mt-2 justify-center">
                      <img src="/reddit-logo.png" alt="Reddit" className="h-4" />
                      <p className="text-gray-500">- Reddit User</p>
                    </div>
                  </div>
                </div>

                {/* Testimonial 2 */}
                <div className="w-full flex-shrink-0 px-4">
                  <div className="bg-white p-6 rounded-xl shadow-md">
                    <p className="text-gray-700 italic">"The bulk actions feature is a game-changer. I can now manage multiple conversations efficiently. This extension has become an essential part of my daily ChatGPT usage."</p>
                    <div className="flex items-center gap-2 mt-2 justify-center">
                      <img src="/reddit-logo.png" alt="Reddit" className="h-4" />
                      <p className="text-gray-500">- Reddit User</p>
                    </div>
                  </div>
                </div>

                {/* Testimonial 3 */}
                <div className="w-full flex-shrink-0 px-4">
                  <div className="bg-white p-6 rounded-xl shadow-md">
                    <p className="text-gray-700 italic">"Finally, a way to organize my ChatGPT conversations! The folder system is exactly what I needed to keep my work organized. Game-changing extension!"</p>
                    <div className="flex items-center gap-2 mt-2 justify-center">
                      <img src="/reddit-logo.png" alt="Reddit" className="h-4" />
                      <p className="text-gray-500">- Reddit User</p>
                    </div>
                  </div>
                </div>

                {/* Testimonial 4 */}
                <div className="w-full flex-shrink-0 px-4">
                  <div className="bg-white p-6 rounded-xl shadow-md">
                    <p className="text-gray-700 italic">"The prompt snippets feature alone is worth it. I've saved so much time by having my most-used prompts ready to go. Highly recommended!"</p>
                    <div className="flex items-center gap-2 mt-2 justify-center">
                      <img src="/reddit-logo.png" alt="Reddit" className="h-4" />
                      <p className="text-gray-500">- Reddit User</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* Navigation Dots */}
            <div className="flex justify-center gap-2 mt-6">
              {[0, 1, 2, 3].map((index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-2.5 h-2.5 rounded-full transition-all ${
                    currentSlide === index ? 'bg-blue-600 w-4' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={() => setCurrentSlide((prev) => (prev > 0 ? prev - 1 : 3))}
              className="absolute left-0 top-1/2 -translate-y-1/2 bg-white p-2 rounded-full shadow-lg hover:bg-gray-50 transition-colors"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={() => setCurrentSlide((prev) => (prev < 3 ? prev + 1 : 0))}
              className="absolute right-0 top-1/2 -translate-y-1/2 bg-white p-2 rounded-full shadow-lg hover:bg-gray-50 transition-colors"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </motion.div>

        <div className="w-full max-w-2xl mx-auto mt-24 mb-24">
          <div className="h-px bg-gray-200"></div>
        </div>

        {/* FAQ Section */}
        <FAQ />

        <div className="w-full max-w-2xl mx-auto mt-24 mb-24">
          <div className="h-px bg-gray-200"></div>
        </div>

        {/* Download Buttons - Bottom Section */}
        <div className="mt-24">
          <DownloadButtons delay={1.0} onButtonClick={handleButtonClick} />
        </div>
      </motion.div>
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/affiliate" element={<AffiliatesPage />} />
        <Route path="/chatgpt-prompt-generator" element={<PromptGeneratorPage />} />
        <Route path="/chatgpt-folders" element={<FoldersPage />} />
        <Route path="/chatgpt-prompt-templates" element={<PromptTemplatesPage />} />
        <Route path="/chatgpt-bulk-actions" element={<BulkActionsPage />} />
        <Route path="/chatgpt-ui" element={<UIPage />} />
        <Route path="/chatgpt-ui-extension" element={<UIExtensionPage />} />
      </Routes>
    </Router>
  );
}